import { isReadOnly } from '@/lib/permissions'
import { useUploadPatientFileMutation } from '@/services/patient-files'
import { borderRadius, colors } from '@/theme'
import CloseIcon from '@mui/icons-material/Close'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography
} from '@mui/material'
import { useRef, useState } from 'react'

const MAX_FILE_SIZE_BYTES = 60 * 1024 * 1024
const ACCEPTED_FILE_TYPES = 'image/*,application/pdf,.heic,.heif'

/** Mirrors the backend upload validation (images and PDFs up to 60 MB). */
const validateFile = (file: File): string | null => {
  const extension = file.name.toLowerCase().split('.').pop()
  const isAllowedType =
    file.type.startsWith('image/') || file.type === 'application/pdf' || extension === 'heic' || extension === 'heif'
  if (!isAllowedType) return 'File must be an image or a PDF'
  if (file.size > MAX_FILE_SIZE_BYTES) return 'File must be 60 MB or smaller'
  return null
}

const formatFileSize = (sizeBytes: number): string => {
  if (sizeBytes < 1024) return `${sizeBytes} B`
  if (sizeBytes < 1024 * 1024) return `${Math.round(sizeBytes / 1024)} KB`
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`
}

interface UploadPatientFileDialogProps {
  open: boolean
  patientHash: string
  /** Order context: uploads are attached to this order and the field becomes read-only. */
  orderNumber?: string
  /** The patient's known order numbers, offered as suggestions for the optional field. */
  orderNumbers?: string[]
  onClose: () => void
}

export default function UploadPatientFileDialog({
  open,
  patientHash,
  orderNumber,
  orderNumbers = [],
  onClose
}: UploadPatientFileDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [orderNumberInput, setOrderNumberInput] = useState('')
  const uploadMutation = useUploadPatientFileMutation(patientHash)

  const handleClose = () => {
    if (uploadMutation.isPending) return
    setFile(null)
    setFileError(null)
    setOrderNumberInput('')
    onClose()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] ?? null
    // Allow re-selecting the same file after clearing.
    event.target.value = ''
    if (!selected) return

    const error = validateFile(selected)
    if (error) {
      setFile(null)
      setFileError(error)
      return
    }
    setFile(selected)
    setFileError(null)
  }

  const handleUpload = async () => {
    if (isReadOnly()) return
    if (!file) return

    try {
      await uploadMutation.mutateAsync({
        file,
        orderNumber: orderNumber ?? (orderNumberInput.trim() || undefined)
      })
      handleClose()
    } catch {
      // The http interceptor already surfaces the error — keep the dialog open.
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{ sx: { borderRadius: borderRadius.md } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Typography variant='sectionTitle'>Upload file</Typography>
          <Typography sx={{ fontSize: 14, color: colors.text.secondary, mt: 0.5 }}>
            Images and PDFs up to 60 MB. The file is visible to the prescriber reviewing this patient.
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size='small' sx={{ mt: -0.5 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <input ref={fileInputRef} type='file' hidden accept={ACCEPTED_FILE_TYPES} onChange={handleFileChange} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button variant='secondary' startIcon={<UploadFileIcon />} onClick={() => fileInputRef.current?.click()}>
            {file ? 'Choose a different file' : 'Choose file'}
          </Button>
          {file && <Chip label={`${file.name} (${formatFileSize(file.size)})`} onDelete={() => setFile(null)} />}
        </Box>
        {fileError && <Typography sx={{ fontSize: 14, color: colors.status.error }}>{fileError}</Typography>}

        {orderNumber ? (
          <TextField
            label='Order number'
            value={orderNumber}
            disabled
            helperText='Files uploaded here are attached to this order'
            fullWidth
          />
        ) : (
          <Autocomplete
            freeSolo
            options={orderNumbers}
            inputValue={orderNumberInput}
            onInputChange={(_, value) => setOrderNumberInput(value)}
            renderInput={params => (
              <TextField
                {...params}
                label='Order number (optional)'
                helperText='Leave empty to attach the file to the patient without an order'
              />
            )}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant='ghost' onClick={handleClose} disabled={uploadMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant='primary'
          onClick={handleUpload}
          disabled={!file || uploadMutation.isPending}
          loading={uploadMutation.isPending}
        >
          Upload file
        </Button>
      </DialogActions>
    </Dialog>
  )
}
